import { Box, Link, palette, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Location from '@solvhealth/types/interfaces/Location';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import { ChevronRight } from '@material-ui/icons';
import { getImageObject } from '~/components/SolvPatternLibrary/Picture/util';
import { formatLocationName, getLocationRatingAndRatingCount } from '~/core/util/location';
import { useLocationSlots } from '~/components/CareTeam/hooks/useSlots';
import Rating from '~/components/Search/components/ResultCard/components/Rating';
import { getHumanReadableDistance } from '~/core/util/geo';
import Preview, {
  DaysContainer,
} from '~/components/Search/components/Card/components/TimeSlots/Preview';
import { SlotSRP } from '~/components/Search/components/Card/components/TimeSlots/components';
import {
  filterSlotsByAvailability,
  getSlotsForTodayAndTomorrow,
  hasSlotsAvailable,
} from '~/core/location/slots';
import NoPreviewView from '~/components/ClinicDetailPageFlexible/components/BookModule/components/variants/NoPreviewView';
import { getViewableSlotsCount } from '~/components/ClinicDetailPageFlexible/components/BookModule/components/variants/NearbyBook/trackingUtils';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  BOOKING_TIMESLOTS_CLICKED,
  CDP_NEARBY_PARTNER_MODULE_CLICKED,
} from '~/core/analytics/events';
import { trackSlotClick } from '~/core/tracking/impression/util';
import useGetTrackingPropertiesFromState from '~/core/tracking/impression/hooks/useGetTrackingPropertiesFromState';

const StyledPreview = styled(Preview).withConfig({ componentId: 'sc-10pmdr4-0' })`
  ${SlotSRP} {
    width: 68px;
    font-size: 12px;
  }
  ${DaysContainer} {
    height: 72px;
  }
`;

type Props = {
  location: Location;
  handleRequestAppointmentClick: (...args: any[]) => void;
  handleRouteToCdpClick?: (...args: any[]) => void;
  setViewableSlots?: React.Dispatch<React.SetStateAction<any>>;
  referringLocation: LocationInterface;
  nearbyVariation: string;
  isMobileView?: boolean;
  index: number;
  visitTypeId?: string;
};

const NearbyLocationCard = ({
  location,
  handleRequestAppointmentClick,
  handleRouteToCdpClick,
  setViewableSlots,
  referringLocation,
  index,
  nearbyVariation,
  visitTypeId,
}: Props) => {
  const { data, isLoading } = useLocationSlots(location.id, visitTypeId);
  const locationWithSlots: LocationInterface | undefined = data?.data;
  const distance = getHumanReadableDistance(location.distanceFromCurrentLocation);
  const { rating, ratingCount } = getLocationRatingAndRatingCount(location);
  const locationHasRatingData = Boolean(rating && ratingCount);
  const image = getImageObject(location, { imageIndex: 0, showDefaultPictureIfMissing: true });
  const trackingProperties = useGetTrackingPropertiesFromState();

  const { today, tomorrow } = getSlotsForTodayAndTomorrow(locationWithSlots);
  const isTodayOrTomorrowSlotsAvailable = hasSlotsAvailable(today) || hasSlotsAvailable(tomorrow);

  const onSlotSelect = (slot: Slot) => {
    handleRequestAppointmentClick(location, slot);
    const slots = location?.slots ?? [...today, ...tomorrow];
    const slotSummary = {
      num_slots_available_today: today.length,
      num_slots_available_tomorrow: tomorrow.length,
      num_slots_available_today_associated_telemed: 0,
      num_slots_available_tomorrow_associated_telemed: 0,
    };
    trackSlotClick(location, trackingProperties, slots, slotSummary, 'nearby_module_slot');
    analyticsTrackEvent(BOOKING_TIMESLOTS_CLICKED, {
      referring_group_id_hash: referringLocation.groups[0].group_id,
      referring_location_id_hash: referringLocation.id,
      version: nearbyVariation,
      group_id_hash: location.groups[0].group_id,
      location_id_hash: location.id,
      is_performance_pricing_enabled: location.isPerformancePricingEnabled,
      is_solv_partner: location.isSolvPartner,
      is_telemed_connect: location.isTelemedConnect,
      action_type: 'timeslot clicked',
    });
  };

  const handleRouteToCdp = () => {
    handleRouteToCdpClick && handleRouteToCdpClick();
    analyticsTrackEvent(CDP_NEARBY_PARTNER_MODULE_CLICKED, {
      referring_group_id_hash: referringLocation.groups[0].group_id,
      referring_location_id_hash: referringLocation.id,
      version: nearbyVariation,
      group_id_hash: location.groups[0].group_id,
      location_id_hash: location.id,
      is_performance_pricing_enabled: location.isPerformancePricingEnabled,
      is_solv_partner: location.isSolvPartner,
      is_telemed_connect: location.isTelemedConnect,
      action: 'click',
      object: 'partner',
      object_type: `position_${index}`,
    });
  };

  const NUMBER_OF_SLOTS_SHOWN_ON_NEARBY_MODULE = 4;

  useEffect(() => {
    if (!isLoading) {
      const numOfTodaySlots = filterSlotsByAvailability(today).length;
      const numOfTomorrowSlots = filterSlotsByAvailability(tomorrow).length;

      const viewableSlots = getViewableSlotsCount(
        NUMBER_OF_SLOTS_SHOWN_ON_NEARBY_MODULE,
        numOfTodaySlots,
        numOfTomorrowSlots
      );

      setViewableSlots?.((prevState: any) => ({
        ...prevState,
        today: prevState.today + viewableSlots.today,
        tomorrow: prevState.tomorrow + viewableSlots.tomorrow,
        cardCount: prevState.cardCount + 1,
      }));
    }
  }, [isLoading]);

  return (
    <Box width="100%">
      <Stack
        alignSelf="stretch"
        bg="white"
        border="solid 1px"
        borderColor="gray-200"
        borderRadius="base"
        boxShadow="xs"
        overflow="hidden"
        width="100%"
      >
        <Stack flex="1" p={2} space={1}>
          <Stack alignItems="center" row>
            <img
              height={48}
              src={`${image.fullRes}?noindex=true`}
              style={{
                borderRadius: '50%',
                overflow: 'hidden',
                objectFit: 'fill',
                flexShrink: 0,
              }}
              width={48}
            />
            <Stack flex="1" ml={2}>
              <Link
                color={palette.gray[600]}
                fontWeight="medium"
                href={`${location.urlSlug}?target=booking`}
                onClick={handleRouteToCdp}
                subtle
                sx={{
                  'div > span': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  },
                }}
              >
                {formatLocationName(location)}
              </Link>
              <Stack alignItems="center" row>
                <Text
                  alignItems="center"
                  as="span"
                  color="gray-500"
                  display="flex"
                  flexShrink={0}
                  minWidth={0}
                  mr={1}
                  variant="subtitle2"
                >
                  {distance}
                </Text>
                {locationHasRatingData && (
                  <Rating rating={rating} ratingCount={ratingCount} showCompact />
                )}
              </Stack>
            </Stack>
            <Link
              fontWeight="bold"
              href={`${location.urlSlug}?target=booking`}
              onClick={handleRouteToCdp}
              sx={{
                color: palette.gray[600],
              }}
            >
              <ChevronRight htmlColor={palette.gray[500]} style={{ fontSize: '24px' }} />
            </Link>
          </Stack>
          {isLoading && (
            <Text alignItems="center" as="div" display="flex" textColor="crunchberry">
              <Spinner color="crunchberry-light" size="xs" />{' '}
              <Box as="span" ml={1}>
                Loading availability
              </Box>
            </Text>
          )}
          {!isLoading && isTodayOrTomorrowSlotsAvailable && (
            <Box alignItems="end" display="flex">
              <StyledPreview
                location={location}
                onSlotSelect={onSlotSelect}
                todaySlots={today.slice(0, NUMBER_OF_SLOTS_SHOWN_ON_NEARBY_MODULE)}
                tomorrowSlots={tomorrow.slice(0, NUMBER_OF_SLOTS_SHOWN_ON_NEARBY_MODULE)}
              />
            </Box>
          )}
          {!isLoading && !isTodayOrTomorrowSlotsAvailable && (
            <NoPreviewView handleRouteToCdpClick={handleRouteToCdpClick} location={location} />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default NearbyLocationCard;
